/**
 * CW-SF-R2 TKT-6 — Win Rate (trailing N days) widget (CEO role).
 *
 * Scalar tile: Won / (Won + Lost) over the trailing window, plus
 * absolute counts in the footer so the rate's noisiness is visible.
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface ApiResponse extends SfWidgetEnvelope {
  days: number;
  won_count: number;
  lost_count: number;
  total_closed: number;
  rate_pct: number | null;
  won_amount: number;
  lost_amount: number;
}

const REFRESH_S = 5 * 60;

export function SfWinRateWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/win-rate', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--win-rate">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.total_closed ?? 0) > 0}
        emptyMessage={`No closed opps in the last ${data?.days ?? 90} days.`}
      >
        <div className="sf-win-rate">
          <div className="sf-win-rate__value">
            {data?.rate_pct !== null && data?.rate_pct !== undefined ? `${data.rate_pct}%` : '—'}
          </div>
          <div className="sf-win-rate__breakdown">
            <span className="sf-win-rate__won">{data?.won_count ?? 0} won</span>
            <span className="sf-win-rate__sep">·</span>
            <span className="sf-win-rate__lost">{data?.lost_count ?? 0} lost</span>
          </div>
          <div className="sf-win-rate__amounts">
            {formatCurrency(data?.won_amount ?? 0)} won
            {' / '}
            {formatCurrency(data?.lost_amount ?? 0)} lost
          </div>
          <div className="sf-win-rate__window">
            trailing {data?.days ?? 90} days
          </div>
        </div>
      </SfWidgetShell>
    </div>
  );
}
